import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FieldsetContainer from '@/components/shared/FieldsetContainer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button, Select, TextInput } from '@/components/ui/daisy';
import LessonPlanItem, {
  LessonPlanItemContentType,
  LessonPlanItemDTO,
} from '@/models/LessonPlanItem';
import { Nullable } from '@/models/Model';
import { lessonPlanItemToFields } from '@/utils/lesson-plan/lessonPlanItemToFields';

import LessonPlanItemAssessmentFields from './LessonPlanItemAssessmentFields';

export type LessonPlanItemFormFields = {
  name: string;
  contentType: LessonPlanItemContentType;
  time: Nullable<number>;
  assessmentId: Nullable<number>;
  assessmentItemId: Nullable<number>;
};

type LessonPlanItemInlineFormProps = {
  coursePathId: number;
  courseLineId: number;
  defaultItem?: LessonPlanItem;
  onSubmit: (data: LessonPlanItemDTO) => void | Promise<unknown>;
  onCancel: () => void;
  isLoading?: boolean;
};

export default function LessonPlanItemInlineForm({
  coursePathId,
  courseLineId,
  defaultItem,
  onSubmit,
  onCancel,
  isLoading,
}: LessonPlanItemInlineFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonPlansTab',
  });

  const methods = useForm<LessonPlanItemFormFields>({
    defaultValues: lessonPlanItemToFields(defaultItem),
  });

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;

  const contentType = useWatch({ control, name: 'contentType' });
  const isEvaluation = contentType === LessonPlanItemContentType.EVALUATION;

  const submit = (fields: LessonPlanItemFormFields) => {
    return onSubmit({
      name: fields.name,
      time: Number(fields.time),
      isEvaluation,
      assessmentItemId: isEvaluation ? fields.assessmentItemId : null,
    });
  };

  return (
    <FormProvider {...methods}>
      <FieldsetContainer>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit(submit)}>
          <TextInput.Fieldset
            label={t('itemName')}
            labelAlt={errors?.name?.message}
          >
            <TextInput
              type="text"
              placeholder={t('itemNamePlaceholder')}
              className="w-full"
              color={!errors?.name?.message ? 'default' : 'error'}
              {...register('name', { required: t('nameRequired') })}
            />
          </TextInput.Fieldset>

          <div className="flex gap-4">
            <Select.Fieldset className="flex-1" label={t('contentType')}>
              <Select
                className="w-full"
                {...register('contentType', {
                  onChange: () => {
                    setValue('assessmentId', null);
                    setValue('assessmentItemId', null);
                  },
                })}
              >
                <Select.Option value={LessonPlanItemContentType.REGULAR}>
                  {t('contentTypeRegular')}
                </Select.Option>
                <Select.Option value={LessonPlanItemContentType.EVALUATION}>
                  {t('contentTypeEvaluation')}
                </Select.Option>
              </Select>
            </Select.Fieldset>

            <TextInput.Fieldset
              className="flex-1"
              label={t('time')}
              labelAlt={errors?.time?.message}
            >
              <TextInput
                type="number"
                min={1}
                placeholder={t('timePlaceholder')}
                className="w-full"
                color={!errors?.time?.message ? 'default' : 'error'}
                {...register('time', {
                  valueAsNumber: true,
                  required: t('timeRequired'),
                  min: { value: 1, message: t('timeRequired') },
                })}
              />
            </TextInput.Fieldset>
          </div>

          <ConditionalRenderer condition={isEvaluation}>
            <LessonPlanItemAssessmentFields
              coursePathId={coursePathId}
              courseLineId={courseLineId}
            />
          </ConditionalRenderer>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              className="grow bg-base-300"
              onClick={onCancel}
              disabled={isLoading}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              color="secondary"
              className="grow"
              disabled={isLoading}
              loading={isLoading}
            >
              {t('save')}
            </Button>
          </div>
        </form>
      </FieldsetContainer>
    </FormProvider>
  );
}
